/**
 * HubBusinessPage — HUB-REDESIGN P5.
 *
 * Bento-grid SaaS analytics surface. Rebuilt from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/other-pages.jsx
 * (BusinessPage). Wired to the existing /hub/business/* endpoints — no
 * fabricated data. Tiles whose data sources are not yet implemented
 * (cohort retention, recent invoices) render with a BACKEND_NEEDED
 * endpoint badge and an empty-state message; the design handoff
 * placeholder rows are deliberately NOT carried into prod.
 *
 * Refresh interval matches HubOverviewPage (30s). The "Last refreshed"
 * footer mirrors HubEngineeringPage.
 */
import { useEffect, useMemo, useState } from 'react'
import { formatTime } from '../utils/dateUtils'
import {
  Card, KPI, Endpoint, AreaChart, Donut, Pill, Icon, DepartmentObservability,
} from './components'
import {
  hubBusiness,
  type HubBusinessMetrics,
  type HubBusinessHistoryPoint,
} from './api'

type WindowDays = 30 | 90 | 365

const PLAN_COLORS = [
  'var(--accent)',
  'var(--info)',
  'var(--ok)',
  'var(--warn)',
  'var(--text-faint)',
]

export default function HubBusinessPage() {
  const [metrics, setMetrics] = useState<HubBusinessMetrics | null>(null)
  const [history, setHistory] = useState<HubBusinessHistoryPoint[] | null>(null)
  const [windowDays, setWindowDays] = useState<WindowDays>(365)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [m, h] = await Promise.all([
        hubBusiness.metrics().catch(() => null),
        hubBusiness.history(windowDays).catch(() => null),
      ])
      if (cancelled) return
      setMetrics(m)
      setHistory(h?.points ?? null)
      setRefreshedAt(new Date())
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [windowDays])

  const mrrSeries = useMemo(
    () => (history ?? []).map(p => p.mrr_cents / 100),
    [history],
  )

  // Derive an "MRR 30d ago" point for the ARR delta sub-text. The
  // history array is in chronological order; the snapshot ~30 days
  // before the latest is at length-30 (or position 0 for shorter
  // windows). Falls back to "annualized" copy when no history exists.
  const arrSubText = useMemo(() => {
    const points = history ?? []
    if (points.length < 2) return 'annualized'
    const idx = Math.max(0, points.length - 31)
    const prior = points[idx]
    if (!prior) return 'annualized'
    const priorArr = prior.arr_cents
    if (!priorArr) return 'annualized'
    return `vs ${fmtUsd(priorArr / 100)} 30d ago`
  }, [history])

  const planSegments = useMemo(() => {
    if (!metrics) return []
    return metrics.plan_breakdown.map((p, i) => ({
      label: planLabel(p.plan),
      count: p.count,
      mrr_cents: p.mrr_cents,
      color: PLAN_COLORS[i % PLAN_COLORS.length],
    }))
  }, [metrics])

  const mrrCents = metrics?.mrr_cents ?? 0
  const arrCents = metrics?.arr_cents ?? 0
  const netNewCents = metrics?.net_new_mrr_cents ?? 0
  const churnedCount = metrics?.cancelled_count ?? 0
  const nrrPct = metrics?.net_revenue_retention_pct ?? 0
  const activeCount = metrics?.active_count ?? 0
  const arpaCents = activeCount > 0 ? Math.round(mrrCents / activeCount) : 0

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Top-row KPI strip — 5 columns separated by 1px borders */}
      <Card bodyPad={false}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1,
          background: 'var(--border)',
        }}>
          <KPI
            label="MRR"
            value={fmtUsd(mrrCents / 100)}
            delta={`${netNewCents >= 0 ? '+' : ''}${fmtUsd(netNewCents / 100)} MTD`}
            tone="up"
            endpoint="/hub/business/metrics"
          />
          <KPI
            label="ARR"
            value={fmtUsd(arrCents / 100)}
            delta={arrSubText}
            tone="up"
          />
          <KPI
            label="Net new"
            value={`${netNewCents >= 0 ? '+' : ''}${fmtUsd(netNewCents / 100)}`}
            delta={`${churnedCount} churned`}
            tone={netNewCents > 0 ? 'up' : netNewCents < 0 ? 'down' : 'flat'}
          />
          <KPI
            label="NRR"
            value={isFinite(nrrPct) && nrrPct > 0 ? `${nrrPct.toFixed(0)}%` : '—'}
            delta="rolling 30d"
            tone="flat"
          />
          <KPI
            label="ARPA"
            value={fmtUsd(arpaCents / 100)}
            delta={`${activeCount} paying`}
            tone="flat"
          />
        </div>
      </Card>

      {/* Second row — MRR trend (8) + Plan mix (4) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 10 }}>
        <Card
          icon="trending-up"
          title="MRR · last 12 months"
          endpoint="/hub/business/metrics"
          right={
            <div className="row" style={{ gap: 4 }}>
              {([
                { label: '30d', days: 30 as WindowDays },
                { label: '90d', days: 90 as WindowDays },
                { label: '12m', days: 365 as WindowDays },
              ]).map(r => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => setWindowDays(r.days)}
                  className="pill ghost"
                  style={{
                    background: windowDays === r.days ? 'var(--surface-3)' : 'transparent',
                    cursor: 'pointer', border: 0,
                    color: windowDays === r.days ? 'var(--text)' : 'var(--text-muted)',
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          }
          style={{ gridColumn: 'span 8' }}
        >
          {mrrSeries.length > 1 ? (
            <AreaChart data={mrrSeries} color="var(--accent)" height={180} />
          ) : (
            <EmptyChart label="No MRR history yet — daily revenue cron seeds first row at 00:01 UTC." height={180} />
          )}
        </Card>

        <Card
          title="Plan mix"
          endpoint="/hub/business/metrics"
          style={{ gridColumn: 'span 4' }}
        >
          <PlanMix segments={planSegments} totalCount={metrics?.active_count ?? 0} trialingCount={metrics?.trialing_count ?? 0} />
        </Card>

        {/* Third row — Cohort retention (6) + Recent invoices (6) */}
        <Card
          title="Cohort retention · trial → active"
          endpoint="BACKEND_NEEDED: cohorts"
          right={<Pill variant="warn">backend-needed</Pill>}
          style={{ gridColumn: 'span 6' }}
        >
          <EmptyState
            icon="users"
            title="Cohort tracking not yet wired"
            body="Trial → active cohort retention requires per-tenant signup-day attribution. See HUB-REDESIGN-GAPS for the backend ticket."
          />
        </Card>

        <Card
          title="Recent invoices"
          endpoint="BACKEND_NEEDED: stripe.invoices"
          right={<Pill variant="warn">backend-needed</Pill>}
          style={{ gridColumn: 'span 6' }}
        >
          <EmptyState
            icon="credit-card"
            title="Invoice list pending Stripe live"
            body="Recent invoices become available once HUB-Stripe-Live ships and the /hub/business/invoices endpoint exists."
          />
        </Card>
      </div>

      <DepartmentObservability department="marketing" />

      {refreshedAt && (
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-dim)' }} className="mono">
          last refreshed {formatTime(refreshedAt)}
          {metrics && !metrics.stripe_live && (
            <span style={{ marginLeft: 8, color: 'var(--warn)' }}>
              · plan-multiplier estimate (Stripe not yet live)
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Plan mix donut + legend ──────────────────────────────────────────
function PlanMix({
  segments, totalCount, trialingCount,
}: {
  segments: { label: string; count: number; mrr_cents: number; color: string }[]
  totalCount: number
  trialingCount: number
}) {
  const drawSegments = segments.length > 0
    ? segments.map(s => ({ value: Math.max(s.count, 0.001), color: s.color }))
    : [{ value: 1, color: 'var(--text-faint)' }]

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
      <Donut size={100} segments={drawSegments} />
      <div style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
        {segments.length === 0 && (
          <div style={{ color: 'var(--text-dim)', padding: '4px 0' }}>No plan data</div>
        )}
        {segments.map(s => (
          <div
            key={s.label}
            className="row"
            style={{ padding: '4px 0', justifyContent: 'space-between', gap: 8 }}
          >
            <span className="row" style={{ gap: 6, overflow: 'hidden', minWidth: 0 }}>
              <span className="dot" style={{ background: s.color }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </span>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-muted)', flexShrink: 0 }}>
              {s.count} · {fmtUsd(s.mrr_cents / 100)}
            </span>
          </div>
        ))}
        {trialingCount > 0 && (
          <div
            className="row"
            style={{ padding: '4px 0', justifyContent: 'space-between', gap: 8 }}
          >
            <span className="row" style={{ gap: 6 }}>
              <span className="dot" style={{ background: 'var(--text-faint)' }} />
              <span>Trialing</span>
            </span>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              {trialingCount}
            </span>
          </div>
        )}
        <div
          className="row"
          style={{
            padding: '6px 0 0', justifyContent: 'space-between', gap: 8,
            borderTop: '1px solid var(--border)', marginTop: 4,
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>Active total</span>
          <span className="mono" style={{ fontSize: 11.5 }}>{totalCount}</span>
        </div>
      </div>
    </div>
  )
}

// ── Empty-state for backend-needed tiles ────────────────────────────
function EmptyState({
  icon, title, body,
}: {
  icon: 'users' | 'credit-card'
  title: string
  body: string
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', textAlign: 'center', gap: 8,
      padding: '28px 16px', color: 'var(--text-muted)',
      border: '1px dashed var(--border)', borderRadius: 6,
      minHeight: 160,
    }}>
      <Icon name={icon} size={20} style={{ color: 'var(--text-dim)' }} />
      <div style={{ fontSize: 12.5, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', maxWidth: 320, lineHeight: 1.5 }}>
        {body}
      </div>
      <Endpoint>BACKEND_NEEDED</Endpoint>
    </div>
  )
}

function EmptyChart({ label, height }: { label: string; height: number }) {
  return (
    <div style={{
      height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-dim)', fontSize: 11, border: '1px dashed var(--border)',
      borderRadius: 6, padding: '0 16px', textAlign: 'center',
    }}>
      {label}
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────────
function fmtUsd(n: number): string {
  if (!isFinite(n)) return '—'
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n).toLocaleString()}`
}

function planLabel(plan: string): string {
  if (!plan) return '—'
  if (plan === 'starter') return 'Starter'
  if (plan === 'pro')     return 'Pro'
  if (plan === 'premium') return 'Premium'
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}
